import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from '@c2fo/liquidity';
import { CaretDownSolidIcon, PencilSlashIcon } from '@c2fo/liquidity/icons';
import { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useLocaleFormat from '@/utils/useLocaleFormat';
import DivisionDetailsChips, { useDivisionDetailsChips } from '../DivisionDetailsChips';

export interface EditInvoicesSidebarProps {
  buyers: BuyerGroupWithStats[];
  onSelectTakerMarket: (tm: TakerMarket) => void;
  resolvedTakerMarket: TakerMarket;
  selectedCurrency: string;
}

interface TakerMarketSidebarItemProps {
  asCurrency: (amount: number, currency: string) => string;
  asNumber: (opts: { value: number }) => string;
  isActive: boolean;
  isLastItem: boolean;
  onClick: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
  tm: TakerMarket;
}

const TakerMarketSidebarItem = ({
  asCurrency,
  asNumber,
  isActive,
  isLastItem,
  onClick,
  t,
  tm,
}: TakerMarketSidebarItemProps) => {
  const { hasAnyChip } = useDivisionDetailsChips(tm);

  return (
    <button
      key={`${tm.makerOrganizationUuid}-${tm.marketUuid}-${tm.takerDivisionId}`}
      type="button"
      className={cn('flex w-full justify-between gap-2 rounded bg-white p-4 text-left shadow-sm last:border-b-0', {
        'bg-lightBlue-500': isActive,
        'border-b-transparent': isLastItem,
      })}
      onClick={onClick}
    >
      <div>
        <div className="text-sm font-medium">{getTakerMarketName(tm)}</div>
        <div className="text-xs">{getTakerMarketDivisionTitle(tm)?.content}</div>
        <div className="text-xs text-text-secondary">{asCurrency(tm.eligibleInvoiceAmount, tm.currency)}</div>
        <div className="text-xs text-text-secondary">
          {`${asNumber({ value: tm.eligibleInvoiceCount })}/${t('core.invoiceWithCount', {
            count: tm.eligibleInvoiceCount + tm.takerExcludedInvoiceCount,
            formattedCount: asNumber({ value: tm.eligibleInvoiceCount + tm.takerExcludedInvoiceCount }),
          })}`}
        </div>
        <div>
          <DivisionDetailsChips displayTooltip={false} size="xs" takerMarket={tm} />
        </div>
      </div>
      {hasAnyChip && (
        <div>
          <PencilSlashIcon className="size-5 fill-text-secondary" />
        </div>
      )}
    </button>
  );
};

const isActiveTakerMarket = (tm: TakerMarket, resolved: TakerMarket) =>
  tm.makerOrganizationUuid === resolved.makerOrganizationUuid &&
  tm.marketUuid === resolved.marketUuid &&
  tm.takerDivisionId === resolved.takerDivisionId;

/**
 * Desktop accordion sidebar — hidden on mobile, shown as a fixed-width column on md+.
 * Rendered outside the scroll container so it doesn't scroll with content.
 */
export const EditInvoicesSidebarDesktop = ({
  buyers,
  onSelectTakerMarket,
  resolvedTakerMarket,
  selectedCurrency,
}: EditInvoicesSidebarProps) => {
  const { t } = useTranslation();
  const { asCurrency, asNumber } = useLocaleFormat();

  return (
    <div className="my-4 ml-4 hidden w-64 shrink-0 overflow-y-auto rounded-xl bg-white p-2 shadow md:block">
      <div className="p-4 pb-6 text-lg font-medium">{t('core.customers')}</div>
      <Accordion
        className="space-y-4"
        defaultValue={buyers.map((buyer) => buyer.makerOrganizationUuid)}
        type="multiple"
      >
        {buyers.map((buyer) => (
          <AccordionItem
            key={buyer.makerOrganizationUuid}
            className="overflow-hidden rounded-xl bg-canvas"
            value={buyer.makerOrganizationUuid}
          >
            <AccordionHeader className="flex items-center justify-between gap-2 bg-blueGray-100 p-4">
              <div>
                <div className="font-medium">{buyer.makerOrganizationName}</div>
                <div className="text-xs">{asCurrency(buyer.stats.eligibleInvoiceAmount, selectedCurrency)}</div>
                <div className="pt-0.5 text-xs">
                  {`${asNumber({ value: buyer.stats.eligibleInvoiceCount })}/${t('core.invoiceWithCount', {
                    count: buyer.stats.eligibleInvoiceCount + buyer.stats.takerExcludedInvoiceCount,
                    formattedCount: asNumber({
                      value: buyer.stats.eligibleInvoiceCount + buyer.stats.takerExcludedInvoiceCount,
                    }),
                  })}`}
                </div>
              </div>
              <div>
                <AccordionTrigger className="size-9" variant="ancillary" />
              </div>
            </AccordionHeader>
            <AccordionContent className="space-y-2 p-2">
              {buyer.takerMarkets.map((tm, tmIdx) => (
                <TakerMarketSidebarItem
                  key={`${tm.makerOrganizationUuid}-${tm.marketUuid}-${tm.takerDivisionId}`}
                  asCurrency={asCurrency}
                  asNumber={asNumber}
                  isActive={isActiveTakerMarket(tm, resolvedTakerMarket)}
                  isLastItem={tmIdx === buyer.takerMarkets.length - 1}
                  onClick={() => onSelectTakerMarket(tm)}
                  t={t}
                  tm={tm}
                />
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

/**
 * Mobile dropdown customer-switcher — hidden on md+, shown on small screens.
 * Rendered inside the scroll container so it scrolls with the content below it.
 */
export const EditInvoicesSidebarMobile = ({
  buyers,
  onSelectTakerMarket,
  resolvedTakerMarket,
  selectedCurrency,
}: EditInvoicesSidebarProps) => {
  const { t } = useTranslation();
  const { asCurrency, asNumber } = useLocaleFormat();

  return (
    <div className="mb-4 rounded-xl bg-white p-4 shadow md:hidden">
      <div className="pb-2 text-lg font-medium">{t('core.customers')}</div>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex w-full items-center justify-between rounded-xl border bg-white px-4 py-3 shadow-sm">
          <div className="text-left">
            <div className="font-medium">{getTakerMarketName(resolvedTakerMarket)}</div>
            <div className="text-sm text-text-secondary">
              {getTakerMarketDivisionTitle(resolvedTakerMarket)?.content}
            </div>
            <div className="text-sm text-text-secondary">
              {asCurrency(resolvedTakerMarket.eligibleInvoiceAmount, resolvedTakerMarket.currency)}
            </div>
          </div>
          <CaretDownSolidIcon className="size-5 shrink-0 fill-text-secondary" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          avoidCollisions={false}
          className="max-h-[60vh] w-[calc(100vw-2rem)] overflow-y-auto"
          side="bottom"
        >
          {buyers.map((buyer, buyerIdx) => (
            <div key={buyer.makerOrganizationUuid}>
              {buyerIdx > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel asChild>
                <div className="bg-blueGray-100">
                  <div className="text-base font-medium text-text-primary">{buyer.makerOrganizationName}</div>
                  <div>{asCurrency(buyer.stats.eligibleInvoiceAmount, selectedCurrency)}</div>
                  <div>
                    {`${asNumber({ value: buyer.stats.eligibleInvoiceCount })}/${t('core.invoiceWithCount', {
                      count: buyer.stats.eligibleInvoiceCount + buyer.stats.takerExcludedInvoiceCount,
                      formattedCount: asNumber({
                        value: buyer.stats.eligibleInvoiceCount + buyer.stats.takerExcludedInvoiceCount,
                      }),
                    })}`}
                  </div>
                </div>
              </DropdownMenuLabel>
              {buyer.takerMarkets.map((tm) => (
                <DropdownMenuItem
                  key={`${tm.makerOrganizationUuid}-${tm.marketUuid}-${tm.takerDivisionId}`}
                  selected={isActiveTakerMarket(tm, resolvedTakerMarket)}
                  onSelect={() => onSelectTakerMarket(tm)}
                >
                  <div>
                    <div className="font-medium">{getTakerMarketName(tm)}</div>
                    <div className="text-sm text-text-secondary">{getTakerMarketDivisionTitle(tm)?.content}</div>
                    <div className="text-sm text-text-secondary">
                      {asCurrency(tm.eligibleInvoiceAmount, tm.currency)}
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
